<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import FairPaginationSegment from './FairPaginationSegment.vue'
import {
  resolveFairPaginationSegmentKind,
  shouldSkipFairPaginationMotion,
} from '@/ui/tokens/fair-pagination-bar.data'

interface FairPaginationBarProps {
  /** Total segments (image count) */
  count?: number
  /** 0-based active index */
  modelValue?: number
  interactive?: boolean
}

const props = withDefaults(defineProps<FairPaginationBarProps>(), {
  count: 1,
  modelValue: 0,
  interactive: true,
})

const emit = defineEmits<{
  'update:modelValue': [index: number]
}>()

const total = computed(() => Math.max(1, props.count))

const indices = computed(() =>
  Array.from({ length: total.value }, (_, i) => i),
)

const instantThumb = ref(false)

watch(
  () => props.modelValue,
  (next, prev) => {
    if (prev == null) return
    if (!shouldSkipFairPaginationMotion(prev, next, total.value)) return
    instantThumb.value = true
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        instantThumb.value = false
      })
    })
  },
)

const thumbStyle = computed(() => ({
  width: `${100 / total.value}%`,
  transform: `translateX(${Math.max(0, props.modelValue) * 100}%)`,
}))

function handleSelect(index: number) {
  if (!props.interactive) {
    return
  }
  emit('update:modelValue', index)
}
</script>

<template>
  <div
    class="fair-pagination-bar"
    role="tablist"
    aria-label="Gallery pages"
  >
    <span
      class="fair-pagination-bar__thumb"
      :class="{
        'fair-pagination-bar__thumb--leading': modelValue <= 0,
        'fair-pagination-bar__thumb--trailing': modelValue >= total - 1,
        'fair-pagination-bar__thumb--instant': instantThumb,
      }"
      :style="thumbStyle"
      aria-hidden="true"
    />
    <FairPaginationSegment
      v-for="index in indices"
      :key="index"
      :kind="resolveFairPaginationSegmentKind(index, indices.length)"
      :active="index === modelValue"
      :interactive="interactive"
      :aria-label="`Image ${index + 1}`"
      @click="handleSelect(index)"
    />
  </div>
</template>
